
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { UserPlus, Search, Eye, Pencil, Trash2, Inbox, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { StudentDetails } from "./StudentDetails";
import { useNavigate } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { useStudentLimit } from "@/hooks/useStudentLimit";
import { RequiredSubscriptionDialog } from "@/components/subscription/RequiredSubscriptionDialog";
import { useLanguagePath } from "@/utils/language";
import { useLanguage } from "@/components/LanguageContext";
import { useTranslations } from "@/hooks/useTranslations";
import { useAuth } from "@/components/AuthProvider";

export const StudentsList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [studentToDelete, setStudentToDelete] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentCount, limitMessage } = useStudentLimit();
  const { getPath } = useLanguagePath();
  const { language } = useLanguage();
  const { t } = useTranslations("students");
  const { user } = useAuth();
  
  const {
    data: students,
    isLoading
  } = useQuery({
    queryKey: ["students", user?.id],
    queryFn: async () => {
      console.log("Fetching students...");
      
      if (!user?.id) {
        console.error("No authenticated user found");
        return [];
      }
      
      // Show ALL students (with and without auth_id) as before
      const {
        data,
        error
      } = await supabase.from("students").select("*")
        .eq("trainer_id", user.id) // Ensure we only get students for this trainer
        .order("created_at", {
          ascending: false
        });
        
      if (error) {
        console.error("Error fetching students:", error);
        return [];
      }
      console.log("Students fetched (all students):", data);
      return data;
    },
    enabled: !!user?.id,
    staleTime: 60000 // 1 minute
  });

  const handleDelete = async (studentId: string) => {
    try {
      if (!user?.id) {
        throw new Error("No authenticated user found");
      }
      
      // First verify this student belongs to the current trainer
      const { data: studentData, error: verifyError } = await supabase
        .from("students")
        .select("id")
        .eq("id", studentId)
        .eq("trainer_id", user.id)
        .single();
        
      if (verifyError || !studentData) {
        throw new Error("Student not found or you don't have permission to delete");
      }
      
      const {
        error
      } = await supabase.from("students").delete().eq("id", studentId);
      if (error) throw error;
      toast.success(t("notifications.deleted"));
      setStudentToDelete(null);
      
      queryClient.invalidateQueries({
        queryKey: ["students"]
      });
      queryClient.invalidateQueries({
        queryKey: ["students-count"]
      });
    } catch (error: any) {
      console.error("Error deleting student:", error);
      toast.error(error.message || t("notifications.errorDeleting"));
    }
  };

  const toggleStatus = async (studentId: string, currentStatus: string) => {
    try {
      if (!user?.id) {
        throw new Error("No authenticated user found");
      }
      
      // First verify this student belongs to the current trainer
      const { data: studentData, error: verifyError } = await supabase
        .from("students")
        .select("id")
        .eq("id", studentId)
        .eq("trainer_id", user.id)
        .single();
        
      if (verifyError || !studentData) {
        throw new Error("Student not found or you don't have permission to update");
      }
      
      console.log("Toggling status for student:", studentId, "Current status:", currentStatus);
      const newStatus = currentStatus === "active" ? "inactive" : "active";
      const {
        error
      } = await supabase.from("students").update({
        status: newStatus
      }).eq("id", studentId);
      if (error) throw error;
      console.log("Status updated successfully to:", newStatus);
      
      queryClient.invalidateQueries({
        queryKey: ["students"]
      });
      
      toast.success(newStatus === "active" ? t("status.activated") : t("status.deactivated"));
    } catch (error: any) {
      console.error("Error toggling student status:", error);
      toast.error(error.message || t("status.error"));
    }
  };

  const handleEdit = (studentId: string) => {
    console.log("Navigating to edit student:", studentId);
    navigate(getPath(`/dashboard/students/edit/${studentId}`));
  };

  const handleNewStudent = () => {
    navigate(getPath("/dashboard/students/new"));
  };

  const handleStudentRequests = () => {
    navigate(getPath("/dashboard/student-requests"));
  };

  const filteredStudents = students?.filter(student => student.first_name.toLowerCase().includes(searchTerm.toLowerCase()) || student.last_name.toLowerCase().includes(searchTerm.toLowerCase()) || student.email?.toLowerCase().includes(searchTerm.toLowerCase()));

  return <div className="space-y-6 animate-fade-in">
      <div className="space-y-2">
        <h2 className="text-3xl font-bold tracking-tight gradient-text text-slate-950">{t("title")}</h2>
        <p className="text-muted-foreground">
          {t("subtitle")}
        </p>
      </div>
      
      <div className="flex items-center justify-between">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href={getPath("/dashboard")} className="text-flame hover:text-flame/80">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="font-medium">{t("title")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        
        <div className="text-sm flex items-center gap-1.5">
          <span className="text-slate-600 dark:text-slate-300">{limitMessage}</span>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder={t("actions.search")} 
            value={searchTerm} 
            onChange={e => setSearchTerm(e.target.value)} 
            className="pl-8 bg-white/80 border border-slate-100" 
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button 
            onClick={() => navigate(getPath("/dashboard/students/new"))}
            className="bg-flame text-white hover:bg-flame/90 rounded-xl shadow-sm hover:shadow-md transition-all"
          >
            <UserPlus className="mr-2 h-4 w-4" />
            {t("actions.newStudent")}
          </Button>
          <Button 
            onClick={() => navigate(getPath("/dashboard/student-requests"))}
            className="bg-slate-gray-light/10 text-slate-gray-dark hover:bg-slate-gray-light/20 rounded-xl shadow-sm"
            variant="secondary"
          >
            <Inbox className="mr-2 h-4 w-4" />
            {t("actions.studentRequests")}
          </Button>
        </div>
      </div>

      {isLoading ? <div className="text-center py-8 bg-white/80 rounded-xl shadow-sm border border-slate-100/50 backdrop-blur-sm">
          <div className="animate-pulse flex flex-col items-center">
            <div className="h-4 w-24 bg-slate-200 rounded mb-3"></div>
            <div className="h-2 w-16 bg-slate-100 rounded"></div>
          </div>
        </div> : <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("table.name")}</TableHead>
              <TableHead className="hidden sm:table-cell">{t("table.email")}</TableHead>
              <TableHead className="hidden sm:table-cell">{t("table.phone")}</TableHead>
              <TableHead>{t("table.status")}</TableHead>
              <TableHead className="text-right">{t("table.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredStudents?.map(student => <TableRow key={student.id} className="hover:bg-slate-50/50">
                <TableCell className="font-medium">
                  {student.first_name} {student.last_name}
                </TableCell>
                <TableCell className="hidden sm:table-cell">
                  {student.email}
                </TableCell>
                <TableCell className="hidden sm:table-cell">
                  {student.phone}
                </TableCell>
                <TableCell>
                  <Switch checked={student.status === "active"} onCheckedChange={() => toggleStatus(student.id, student.status || "inactive")} />
                </TableCell>
                <TableCell className="text-right space-x-2">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedStudent(student)} className="hover:bg-flame/10 hover:text-flame">
                    <Eye className="h-4 w-4" />
                    <span className="sr-only">{t("actions.viewDetails")}</span>
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(student.id)} className="hover:bg-flame/10 hover:text-flame">
                    <Pencil className="h-4 w-4" />
                    <span className="sr-only">{t("actions.edit")}</span>
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setStudentToDelete(student.id)} className="hover:bg-destructive/10 hover:text-destructive">
                    <Trash2 className="h-4 w-4" />
                    <span className="sr-only">{t("actions.delete")}</span>
                  </Button>
                </TableCell>
              </TableRow>)}
            {filteredStudents?.length === 0 && <TableRow>
                <TableCell colSpan={5} className="text-center py-4">
                  {t("table.noStudentsFound")}
                </TableCell>
              </TableRow>}
          </TableBody>
        </Table>}

      {selectedStudent && <StudentDetails student={selectedStudent} isOpen={!!selectedStudent} onClose={() => setSelectedStudent(null)} />}

      <AlertDialog open={!!studentToDelete} onOpenChange={() => setStudentToDelete(null)}>
        <AlertDialogContent className="bg-white/95 backdrop-blur-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deleteDialog.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("deleteDialog.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => studentToDelete && handleDelete(studentToDelete)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("deleteDialog.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>;
};
